import React from "react"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { DEFAULT_MAX_LENGTH } from "shared/constants/app-constants"
import { useEvent } from "shared/lib/useEvent"
import { InstallationReference } from "shared/types/InstallationReference"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { ImageCaption } from "shared/ui/image-caption/ImageCaption"
import { MarkdownInput } from "shared/ui/input/markdown/MarkdownInput"

import { useProductPositionFormFeaturesContext } from "../../contexts/ProductPositionFormFeaturesContext"

interface ProductPositionInstallationReferenceListItemProps {
  namePrefix: string
  installationReference: InstallationReference | null | undefined
  isEditing?: boolean
  onDelete?: () => void
}

export const ProductPositionInstallationReferenceListItem = React.memo(
  ({
    namePrefix,
    installationReference,
    isEditing,
    onDelete
  }: ProductPositionInstallationReferenceListItemProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { openInstallationReferenceImage } =
      useProductPositionFormFeaturesContext()

    const value = useWatch({ name: `${namePrefix}.title` })

    const buildingRef = installationReference?.__obj?.parent()
    const buildingRefTitleSlug = buildingRef
      ?.get("title")
      ?.toString()
      .replace(/\s+/g, "-")
      .toLowerCase()
    const buildingRefID = buildingRef?.id()
    const installationRefID = installationReference?.__obj?.get("referenceID")

    const backlink =
      buildingRefTitleSlug && buildingRefID && installationRefID
        ? `${buildingRefTitleSlug}-${buildingRefID}#installation-${installationRefID}`
        : null

    const handleNavigateToReferences = useEvent(() => {
      if (backlink) navigate(backlink)
    })

    const handleRoomImageClick = useEvent(() => {
      if (!installationReference?.roomImage) return
      openInstallationReferenceImage?.(
        installationReference.roomImage,
        t("InstallationReference.RoomImage")
      )
    })

    const handleDetailImageClick = useEvent(() => {
      if (!installationReference?.detailImage) return
      openInstallationReferenceImage?.(
        installationReference.detailImage,
        t("InstallationReference.DetailImage")
      )
    })

    return (
      <div className="product-position-install-ref-list__item">
        <div className="product-position-install-ref-list__item-header">
          <h3 className="product-position-install-ref-list__item-title">
            {installationReference?.title}
          </h3>
          {!isEditing && onDelete ? (
            <Button
              id="product-position-install-ref-list-delete"
              variant={ButtonVariant.SECONDARY}
              className="btn--borderless btn--bleed"
              isIconOnly
              icon={IconName.BIN}
              onClick={onDelete}
              label={t("InstallationReference.DeleteConfirmation.Title")}
            />
          ) : null}
        </div>
        {isEditing || value ? (
          <MarkdownInput.Root
            label={t("General.Remark")}
            maxLength={DEFAULT_MAX_LENGTH}
            name={`${namePrefix}.title`}
            readonly={!isEditing}
          >
            <MarkdownInput.Container maxHeight={null}>
              <MarkdownInput.Input />
              <MarkdownInput.Counter />
              <MarkdownInput.Readonly />
            </MarkdownInput.Container>
          </MarkdownInput.Root>
        ) : null}
        <div className="product-position-install-ref-list__images">
          <Button
            className="product-image__zoom-button"
            style={{ cursor: "zoom-in" }}
            onClick={handleRoomImageClick}
            label={t("InstallationReference.RoomImage")}
            disabled={!installationReference}
          >
            <ImageCaption
              caption={t("InstallationReference.RoomImage")}
              isLoading={!installationReference}
            >
              {installationReference?.roomImage}
            </ImageCaption>
          </Button>
          <Button
            className="product-image__zoom-button"
            style={{ cursor: "zoom-in" }}
            onClick={handleDetailImageClick}
            label={t("InstallationReference.DetailImage")}
            disabled={!installationReference}
          >
            <ImageCaption
              caption={t("InstallationReference.DetailImage")}
              isLoading={!installationReference}
            >
              {installationReference?.detailImage}
            </ImageCaption>
          </Button>
        </div>
        {backlink ? (
          <div className="product-position-install-ref-list__backlink">
            <Button
              id="product-position-install-ref-list-backlink"
              variant="link"
              onClick={handleNavigateToReferences}
            >
              {t("BuildingReference.OpenReferencesPage")}
            </Button>
          </div>
        ) : null}
      </div>
    )
  }
)
